import { router } from '@inertiajs/react'
import { useState } from 'react'
import { plan_top_down_path } from '@/lib/routes'
import type { PlanNode } from '../components/PlanNodesPanel'

// Finds the plan row (at any depth) that sits on the given product group.
export function findByProductNodeId(nodes: PlanNode[], productNodeId: string): PlanNode | null {
  for (const n of nodes) {
    if (n.productNodeId === productNodeId) return n
    const child = findByProductNodeId(n.children, productNodeId)
    if (child) return child
  }
  return null
}

// The row one level above the given row; null when the row is at the top level.
export function parentDrillId(nodes: PlanNode[], rowId: string, parentId: string | null = null): string | null {
  for (const n of nodes) {
    if (n.id === rowId) return parentId
    const found = parentDrillId(n.children, rowId, n.id)
    if (found) return found
  }
  return null
}

export function findNode(nodes: PlanNode[], id: string): PlanNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    const child = findNode(n.children, id)
    if (child) return child
  }
  return null
}

export function findPath(nodes: PlanNode[], id: string, trail: PlanNode[] = []): PlanNode[] | null {
  for (const n of nodes) {
    const path = [...trail, n]
    if (n.id === id) return path
    const child = findPath(n.children, id, path)
    if (child) return child
  }
  return null
}

// Which plan row the left-hand picker is drilled into, and the page reload
// that fetches that row's product group revenue.
export function usePickerDrill({
  marketSlug,
  slug,
  planNodes
}: {
  marketSlug: string
  slug: string
  planNodes: PlanNode[]
}) {
  const [drillNodeId, setDrillNodeId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    const groupId = new URLSearchParams(window.location.search).get('drill_product_node_id')
    if (!groupId) return null
    return findByProductNodeId(planNodes, groupId)?.id ?? null
  })

  const reloadRevenue = (productNodeId: string | null) => {
    router.get(
      plan_top_down_path(marketSlug, slug),
      productNodeId ? { drill_product_node_id: productNodeId } : {},
      { preserveScroll: true, preserveState: true, only: ['node_revenue', 'period'] }
    )
  }

  const drillTo = (rowId: string | null) => {
    if (rowId == null) {
      setDrillNodeId(null)
      reloadRevenue(null)
      return
    }
    const row = findNode(planNodes, rowId)
    if (!row?.productNodeId) return
    setDrillNodeId(rowId)
    reloadRevenue(row.productNodeId)
  }

  const handleDrill = (node: PlanNode) => {
    if (!node.productNodeHasChildren || node.special || node.isNewProduct) return
    drillTo(drillNodeId === node.id ? null : node.id)
  }

  const handleNavigate = (id: string | null) => {
    if (id === drillNodeId) return
    drillTo(id)
  }

  const handleClearDrill = () => {
    drillTo(drillNodeId ? parentDrillId(planNodes, drillNodeId) : null)
  }

  // Leave the drilled level without a new fetch decision by the caller
  // (used when the drilled row itself is removed).
  const resetDrill = () => {
    setDrillNodeId(null)
    reloadRevenue(null)
  }

  return { drillNodeId, handleDrill, handleNavigate, handleClearDrill, resetDrill }
}
